import { Component } from '@theme/component';
import {
  ReadingStartedEvent,
  ReadingQuestionAnsweredEvent,
  ReadingCompletedEvent,
  ReadingArchetypeProducedEvent,
  ReadingProductClickedEvent,
} from '@theme/events';

/**
 * Publishes a custom event to Shopify's Customer Events / pixel pipeline, when available.
 * Safe to call unconditionally -- Shopify.analytics is only present once the storefront's
 * analytics script has loaded, which happens by default on every Shopify storefront.
 *
 * @param {string} name
 * @param {Record<string, unknown>} [payload]
 */
function publishAnalyticsEvent(name, payload = {}) {
  try {
    // @ts-ignore -- Shopify's global analytics object isn't typed here.
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch {
    // Analytics must never break the quiz.
  }
}

/**
 * The YUSUFZ Reading quiz. Reads its questions, answers, and possible archetype results
 * straight from server-rendered blocks (see blocks/reading-question.liquid,
 * blocks/reading-answer.liquid, blocks/reading-archetype.liquid) -- there's no separate
 * data payload to keep in sync. Scoring is a simple deterministic tally: each answer lists
 * the archetype slug(s) it weights toward, and whichever archetype accumulates the most
 * points across the whole quiz wins (first-defined wins on a tie).
 *
 * @typedef {{
 *   introPanel: HTMLElement,
 *   progressText: HTMLElement,
 *   questionPanels: HTMLFieldSetElement[],
 *   resultPanels: HTMLElement[],
 * }} ReadingQuizRefs
 *
 * @extends {Component<ReadingQuizRefs>}
 */
export class ReadingQuizComponent extends Component {
  requiredRefs = ['introPanel', 'progressText'];

  /** @type {Record<string, number>} */
  #scores = {};

  /** @type {number} */
  #questionIndex = 0;

  get #storageKey() {
    return this.dataset.storageKey || 'yusufz-reading-result';
  }

  connectedCallback() {
    super.connectedCallback();

    const urlSlug = new URLSearchParams(window.location.search).get('read');
    const storedSlug = urlSlug || this.#readStoredSlug();
    const panel = storedSlug && this.#findResultPanel(storedSlug);

    if (panel) {
      this.#showResult(panel, { announce: false });
    }
  }

  /** Begins the quiz: hides the intro, shows the first question. */
  start() {
    this.refs.introPanel.hidden = true;
    this.refs.progressText.hidden = false;
    this.#questionIndex = 0;
    this.#scores = {};
    this.#showQuestion(0);

    document.dispatchEvent(new ReadingStartedEvent());
    publishAnalyticsEvent('reading_started');
  }

  /**
   * Handles an answer click: tallies its archetype slugs, then advances to the next
   * question or finishes the quiz.
   *
   * @param {Event} event
   */
  selectAnswer(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    const slugs = (button.dataset.slugs || '')
      .split(',')
      .map((slug) => slug.trim())
      .filter(Boolean);

    for (const slug of slugs) {
      this.#scores[slug] = (this.#scores[slug] || 0) + 1;
    }

    const questionPanels = this.refs.questionPanels || [];

    document.dispatchEvent(new ReadingQuestionAnsweredEvent(this.#questionIndex, questionPanels.length, slugs));
    publishAnalyticsEvent('reading_question_answered', {
      questionIndex: this.#questionIndex,
      questionCount: questionPanels.length,
      slugs,
    });

    const nextIndex = this.#questionIndex + 1;

    if (nextIndex < questionPanels.length) {
      this.#showQuestion(nextIndex);
    } else {
      this.#finish();
    }
  }

  /** Tallies the final scores and reveals the winning archetype's result panel. */
  #finish() {
    const resultPanels = this.refs.resultPanels || [];
    let winner = resultPanels[0];
    let winningScore = -1;

    for (const panel of resultPanels) {
      const slug = panel.dataset.archetype || '';
      const score = this.#scores[slug] || 0;

      if (score > winningScore) {
        winningScore = score;
        winner = panel;
      }
    }

    if (!winner) return;

    document.dispatchEvent(new ReadingCompletedEvent());
    publishAnalyticsEvent('reading_completed');

    this.#showResult(winner, { announce: true });
  }

  /**
   * Shows a result panel, hides every other result/question panel, and persists the
   * result to localStorage + the URL so it's shareable and survives a reload.
   *
   * @param {HTMLElement} panel
   * @param {{ announce: boolean }} options
   */
  #showResult(panel, { announce }) {
    this.refs.introPanel.hidden = true;

    for (const questionPanel of this.refs.questionPanels || []) {
      questionPanel.hidden = true;
    }

    for (const resultPanel of this.refs.resultPanels || []) {
      resultPanel.hidden = resultPanel !== panel;
    }

    const slug = panel.dataset.archetype || '';

    this.#persistResult(slug);

    if (announce) {
      // Repurpose the live region (it was left reading a stale "Question 8 / 8")
      // so the result is actually announced, then move focus into the panel.
      const heading = panel.querySelector('h1, h2, h3');
      this.refs.progressText.hidden = false;
      this.refs.progressText.textContent = `Your read: ${heading ? heading.textContent.trim() : slug}`;
      this.#focusPanelHeading(panel);
    } else {
      this.refs.progressText.hidden = true;
    }

    if (announce) {
      document.dispatchEvent(new ReadingArchetypeProducedEvent(slug));
      publishAnalyticsEvent('reading_archetype_produced', { slug });
    }
  }

  /** Clears the stored result and restarts the quiz from the first question. */
  retake() {
    this.#clearStoredResult();

    for (const resultPanel of this.refs.resultPanels || []) {
      resultPanel.hidden = true;
    }

    this.refs.introPanel.hidden = true;
    this.refs.progressText.hidden = false;
    this.#questionIndex = 0;
    this.#scores = {};
    this.#showQuestion(0);
  }

  /**
   * Shares the current result. Tries, in order: sharing a generated "MY YUSUFZ READ"
   * image card (Web Share API with a file), sharing just the link (Web Share API,
   * text only), then copying the link to the clipboard. Card generation is best-effort
   * and wrapped so any failure (CORS, canvas support, etc.) just falls through to the
   * next option -- it never blocks the basic link share.
   *
   * @param {Event} event
   */
  async shareResult(event) {
    const panel = /** @type {HTMLElement} */ (event.target).closest('[data-archetype]');
    const slug = panel instanceof HTMLElement ? panel.dataset.archetype : '';
    const name = panel?.querySelector('.reading-quiz__result-name')?.textContent?.trim() || 'my YUSUFZ read';
    const traits = panel?.querySelector('.reading-quiz__result-traits')?.textContent?.trim() || '';
    const imageSrc = panel?.querySelector('.reading-quiz__result-image')?.getAttribute('src') || '';

    const url = new URL(window.location.href);
    url.searchParams.set('read', slug || '');

    const shareData = {
      title: `YUSUFZ Reading: ${name}`,
      text: `I got "${name}" on the YUSUFZ Reading.`,
      url: url.toString(),
    };

    const cardBlob = imageSrc ? await this.#buildShareCardBlob({ name, traits, imageSrc }) : null;

    if (cardBlob && navigator.canShare) {
      const file = new File([cardBlob], 'yusufz-reading.png', { type: 'image/png' });

      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ ...shareData, files: [file] });
          return;
        } catch {
          // Fall through (also covers the user cancelling the share sheet).
        }
      }
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // Fall through to clipboard copy.
      }
    }

    try {
      await navigator.clipboard.writeText(url.toString());
    } catch {
      // Clipboard access can fail (permissions, insecure context); nothing further to do here.
    }
  }

  /**
   * Composes a branded, Instagram-portrait "MY YUSUFZ READ" card on an offscreen
   * canvas: the archetype photo, a dark gradient for legibility, the YUSUFZ wordmark,
   * and the archetype's name/traits. Returns null (never throws) if anything about
   * this fails, so the caller can fall back to a plain link share.
   *
   * @param {{ name: string, traits: string, imageSrc: string }} data
   * @returns {Promise<Blob|null>}
   */
  async #buildShareCardBlob({ name, traits, imageSrc }) {
    try {
      const width = 1080;
      const height = 1350;

      const logoSrc = this.dataset.logoSrc || '';
      const imageLoads = [this.#loadImage(imageSrc)];
      if (logoSrc) imageLoads.push(this.#loadImage(logoSrc));

      const [photo, logo] = await Promise.all(imageLoads);

      if (document.fonts?.ready) await document.fonts.ready;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const photoScale = Math.max(width / photo.width, height / photo.height);
      const photoWidth = photo.width * photoScale;
      const photoHeight = photo.height * photoScale;
      ctx.drawImage(photo, (width - photoWidth) / 2, (height - photoHeight) / 2, photoWidth, photoHeight);

      const gradient = ctx.createLinearGradient(0, height * 0.45, 0, height);
      gradient.addColorStop(0, 'rgba(13, 13, 13, 0)');
      gradient.addColorStop(1, 'rgba(13, 13, 13, 0.92)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      if (logo) {
        const logoHeight = 40;
        const logoWidth = logo.width * (logoHeight / logo.height);
        ctx.drawImage(logo, 64, 64, logoWidth, logoHeight);
      }

      ctx.fillStyle = '#F3F0E9';
      ctx.textBaseline = 'alphabetic';

      ctx.font = '600 28px Archivo, sans-serif';
      ctx.fillText('MY YUSUFZ READ', 64, height - 220);

      ctx.font = '700 76px Archivo, sans-serif';
      this.#wrapText(ctx, name.toUpperCase(), 64, height - 150, width - 128, 82);

      if (traits) {
        ctx.font = '400 30px Archivo, sans-serif';
        ctx.fillStyle = 'rgba(243, 240, 233, 0.75)';
        ctx.fillText(traits, 64, height - 64);
      }

      return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    } catch {
      return null;
    }
  }

  /**
   * @param {string} src
   * @returns {Promise<HTMLImageElement>}
   */
  #loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  /**
   * Minimal canvas text wrapping for the (short) archetype name.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} text
   * @param {number} x
   * @param {number} y
   * @param {number} maxWidth
   * @param {number} lineHeight
   */
  #wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    const lines = [];
    let line = '';

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    }
    if (line) lines.push(line);

    const startY = y - (lines.length - 1) * lineHeight;
    lines.forEach((lineText, index) => ctx.fillText(lineText, x, startY + index * lineHeight));
  }

  /** @param {Event} event */
  productClicked(event) {
    const link = /** @type {HTMLElement} */ (event.target);
    const panel = link.closest('[data-archetype]');
    const slug = panel instanceof HTMLElement ? panel.dataset.archetype || '' : '';
    const productUrl = link instanceof HTMLAnchorElement ? link.href : '';

    document.dispatchEvent(new ReadingProductClickedEvent(slug, productUrl));
    publishAnalyticsEvent('reading_product_clicked', { slug, productUrl });
  }

  /** @param {number} index */
  #showQuestion(index) {
    const questionPanels = this.refs.questionPanels || [];

    questionPanels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });

    this.#questionIndex = index;
    this.refs.progressText.textContent = `Question ${index + 1} / ${questionPanels.length}`;

    // The button that was just clicked is now hidden, so focus would otherwise
    // fall to <body> and a keyboard user would be dumped back at the top of the
    // document between every question (WCAG 2.4.3). Move it to the new question
    // instead; the legend is the question text, so this also announces it.
    this.#focusPanelHeading(questionPanels[index]);
  }

  /**
   * Moves focus to a panel's heading/legend so the step change is announced and
   * keyboard focus stays where the content is.
   *
   * @param {HTMLElement | undefined} panel
   */
  #focusPanelHeading(panel) {
    if (!panel) return;

    const target = panel.querySelector('legend, h1, h2, h3') || panel;
    if (!(target instanceof HTMLElement)) return;

    // Programmatic-only focus: keeps it out of the Tab order but lets the
    // screen reader land on (and read) the new heading.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: false });
  }

  /** @param {string} slug */
  #findResultPanel(slug) {
    return (this.refs.resultPanels || []).find((panel) => panel.dataset.archetype === slug);
  }

  /** @param {string} slug */
  #persistResult(slug) {
    try {
      window.localStorage.setItem(this.#storageKey, slug);
    } catch {
      // localStorage can be unavailable (private browsing, storage disabled); the URL
      // param still makes the result shareable even if it doesn't persist locally.
    }

    const url = new URL(window.location.href);
    url.searchParams.set('read', slug);
    window.history.replaceState(window.history.state, '', url);
  }

  #clearStoredResult() {
    try {
      window.localStorage.removeItem(this.#storageKey);
    } catch {
      // See #persistResult.
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('read');
    window.history.replaceState(window.history.state, '', url);
  }

  #readStoredSlug() {
    try {
      return window.localStorage.getItem(this.#storageKey);
    } catch {
      return null;
    }
  }
}

if (!customElements.get('reading-quiz-component')) {
  customElements.define('reading-quiz-component', ReadingQuizComponent);
}
